// The version's state on the desk (DD, 2026-10-22): a draft can be issued to
// the client, which freezes its prices; any version can be replaced by a new
// one at today's rates, after the reprice check — each job price against
// today's book, Keep (schedule of rates) or Use book price (the usual case).
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@higgins/ui';
import { isFrozenVersion, jobPricesOf, repriceCheck, type RepriceRow } from '@higgins/m2q-pricing';
import { ApiError, getPricingReference, isConnectionFailure, issueVersionApi, newVersionApi, resolvePaints, type RepriceDecisionToApi } from '../lib/api';
import { isoToday, formatDate } from '../lib/dates';
import type { DeviceRecord } from '../lib/device-db';
import { formatMoney } from './surface-format';
import { ErrorMessage, Notice } from './message';

interface Props {
  measure: DeviceRecord;
  version: DeviceRecord;
  options: DeviceRecord[];
  records: DeviceRecord[];
  /** Why the price can't go to the client yet (REVIEW questions, unsynced changes); empty = it can. */
  issueBlockers: string[];
  onChanged: () => void;
}

/** How a hire line's two prices are named on the check. */
const PART_NAMES = { dayRate: 'day rate', transport: 'cartage' } as const;

const describe = (err: unknown, what: string) => (isConnectionFailure(err) ? `${what} needs signal.` : err instanceof ApiError ? err.message : `${what} didn't work.`);

/** Today's book prices for the job's priced items, and the check's rows. */
async function loadCheck(measure: DeviceRecord, records: DeviceRecord[]): Promise<RepriceRow[]> {
  const prices = jobPricesOf(records, measure.id);
  const productIds = prices.flatMap((p) => (p.paintProductId ? [p.paintProductId] : []));
  const today = isoToday();
  const [reference, resolved] = await Promise.all([getPricingReference(measure.jobId, today), productIds.length ? resolvePaints(productIds, today) : Promise.resolve({ products: {} })]);
  const products = resolved.products as Record<string, { costPerLitre: number } | null>;
  return repriceCheck(prices, {
    paint: (id) => products[id.toLowerCase()]?.costPerLitre ?? null,
    hire: (id) => reference.hireItems.find((h) => h.id.toLowerCase() === id.toLowerCase()) ?? null,
  });
}

function RepriceCheck({ rows, onMake, onCancel, busy }: { rows: RepriceRow[]; onMake: (decisions: RepriceDecisionToApi[]) => void; onCancel: () => void; busy: boolean }) {
  const [keep, setKeep] = useState<Set<string>>(new Set());
  const inBook = rows.filter((r) => r.inBook);
  const keyOf = (r: RepriceRow) => `${r.id}:${r.part}`;
  const toggle = (key: string, on: boolean) => setKeep((k) => (on ? new Set(k).add(key) : new Set([...k].filter((x) => x !== key))));
  const decisions = inBook.map((r) => ({ jobPriceId: r.id, part: r.part, decision: keep.has(keyOf(r)) ? ('keep' as const) : ('book' as const) }));
  return (
    <div className="mt-2 space-y-2 text-sm">
      {rows.length === 0 ? (
        <p className="text-slate-600">This job has no job prices, so the new version simply uses today&rsquo;s book.</p>
      ) : (
        <>
          <p className="text-slate-600">Job prices against today&rsquo;s book. Keep a job price for a schedule-of-rates job; otherwise use the book price.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500">
                  <th className="py-1 pr-2">Item</th><th className="pr-2 text-right">Job price</th><th className="pr-2 text-right">Book then</th><th className="pr-2 text-right">Book now</th><th>Choice</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={keyOf(r)} className="border-t border-slate-100">
                    <td className="py-1 pr-2">
                      {r.name}
                      {r.part !== 'price' && <span className="text-xs text-slate-500"> · {PART_NAMES[r.part]}</span>}
                    </td>
                    <td className="pr-2 text-right tabular-nums">{formatMoney(r.jobPrice)}</td>
                    <td className="pr-2 text-right tabular-nums">{r.bookThen === null ? '—' : `${formatMoney(r.bookThen)}${r.bookThenDate ? ` (${formatDate(r.bookThenDate)})` : ''}`}</td>
                    <td className="pr-2 text-right tabular-nums">{r.bookNow === null ? '—' : formatMoney(r.bookNow)}</td>
                    <td>
                      {r.inBook ? (
                        <label className="flex items-center gap-1">
                          <input type="checkbox" checked={keep.has(keyOf(r))} onChange={(e) => toggle(keyOf(r), e.target.checked)} className="h-4 w-4" /> Keep job price
                        </label>
                      ) : (
                        <span className="text-xs text-slate-500">kept (not in a book)</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {inBook.length > 1 && (
            <button type="button" onClick={() => setKeep(new Set())} className="text-xs text-primary underline">
              Use book for all
            </button>
          )}
        </>
      )}
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onMake(decisions)} disabled={busy}>
          {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Make the new version
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function VersionPanel({ measure, version, options, records, issueBlockers, onChanged }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [check, setCheck] = useState<RepriceRow[] | null>(null);
  const status = String(version.fields.hig_status ?? 'Draft');
  const frozen = isFrozenVersion(status);
  const accepted = options.some((o) => o.fields.hig_accepted === true);
  const changed = () => {
    setCheck(null);
    onChanged();
  };

  /** Run `work`, then `done` with its result; a failure is said plainly. */
  async function run<T>(work: () => Promise<T>, what: string, done: (result: T) => void) {
    setBusy(true);
    setError(null);
    try {
      done(await work());
    } catch (err) {
      setError(describe(err, what));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-900">{String(version.fields.hig_name ?? '')}</span>
        <span className={`rounded-full px-2 text-xs ${frozen ? 'bg-slate-200 text-slate-700' : 'bg-amber-100 text-amber-900'}`}>
          {status}
          {status === 'Issued' && version.fields.hig_issuedon ? ` ${formatDate(version.fields.hig_issuedon)}` : ''}
        </span>
        {!frozen && (
          <Button size="sm" variant="outline" disabled={busy || issueBlockers.length > 0} onClick={() => void run(() => issueVersionApi(version.id), 'Issuing', changed)}>
            Issue to client
          </Button>
        )}
        {!accepted && !check && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void run(() => loadCheck(measure, records), 'The reprice check', setCheck)}>
            New version at today&rsquo;s rates
          </Button>
        )}
      </div>
      {!frozen && issueBlockers.length > 0 && <p className="mt-1 text-xs text-slate-500">Issue once: {issueBlockers.join('; ')}.</p>}
      {frozen && <Notice className="mt-2">This version&rsquo;s prices are frozen{status === 'Issued' ? ' as sent to the client' : ''}. Make a new version to change them.</Notice>}
      {check && <RepriceCheck rows={check} busy={busy} onCancel={() => setCheck(null)} onMake={(decisions) => void run(() => newVersionApi(version.id, decisions), 'Making the new version', changed)} />}
      {error && <ErrorMessage className="mt-2">{error}</ErrorMessage>}
    </div>
  );
}
